var Layout    = require("app/ui/Layout"),
    get       = require("object-path").get,
    kind      = require("enyo/kind"),
    themeVars = require("app/theme/variables.js");

module.exports = kind(/** @lends  app.AppHeader.HeaderLayout.prototype */ {

    /**
     * @private
     */
    name: "app.AppHeader.HeaderLayout",

    /**
     * @private
     */
    kind: Layout,

    /**
     * ```
     * {
     *   backButton:        false,
     *   logo:              false,
     *   previewModeButton: false,
     *   searchButton:      false,
     *   playlistsButton:   false,
     *   bookmarksButton:   false,
     *   genresButton:      false,
     *   artistsButton:     false,
     *   categoryDropdown:  false,
     *   brandDropdown:     false,
     *   signUpButton:      false,
     *   loginButton:       false,
     *   languageSwitcher:  false,
     *   menuDropdown:      false
     * }
     * ```
     *
     * @private
     */
    headerControlVisibility: null,

    /**
     * @public
     */
    reflow: function() {
        var $container      = this.container,
            containerNode   = $container.hasNode(),
            canShowAllHeaderControls,
            user            = $container.app.$.authService.user,
            $previewModeBar = $container.$.previewModeBar;

        if (!containerNode) {
            return;
        }

        // Initially we'll hide all dropdown buttons. We'll re-enable the ones
        // we need later.
        this.hideMenuDropdownControls();

        // We visually hide the header controls to make sure we don't get
        // half-finished layouts drawn on screen.
        this.visuallyHideHeaderControls();

        // We also reset the header control visibility, hiding all header
        // content by default
        this.resetHeaderControlsVisibility();

        // First we will attempt to display all buttons, including captions, in
        // the header bar.
        this.enableHeaderButtonCaptions();
        canShowAllHeaderControls = this.reflowHeader();

        // If we cannot fit all buttons with caption in the header bar we can
        // try removing the captions of some of them and show them as icon-only.
        // This needs to be enabled with a flag in themeVars.
        if (!canShowAllHeaderControls && themeVars.appHeaderHasIconMode) {
            this.resetHeaderControlsVisibility();
            this.disableHeaderButtonCaptions();
            canShowAllHeaderControls = this.reflowHeader();
        }

        // If we still cannot fit all buttons in the  header bar, either with or
        // without captions, we can move all optional header buttons into the
        // dropdown menu instead of only the ones that didn't fit. This needs to
        // be enabled with a flag in themeVars.
        if (!canShowAllHeaderControls && themeVars.appHeaderMoveAllButtonsToMenuDropdown) {
            this.moveAllButtonsToMenuDropdown();
        }

        // Work out which buttons to show in the header menu
        this.showHeaderControls();

        // Work out which buttons to show in the dropdown menu
        this.showMenuDropdownControls();

        // Add a preview bar if needed
        if (get(user, "preview_mode_type", false) && get(user, "preview_mode", false)) {
            $previewModeBar.show();
        }
        else {
            $previewModeBar.hide();
        }
    },

    /**
     * @private
     */
    hideMenuDropdownControls: function() {
        var $menuDropdown = this.container.$.menuDropdown;

        this.hideControl($menuDropdown.$.previewModeButton);
        this.hideControl($menuDropdown.$.signUpButton);
        this.hideControl($menuDropdown.$.loginButton);
        this.hideControl($menuDropdown.$.searchButton);
        this.hideControl($menuDropdown.$.playlistsButton);
        this.hideControl($menuDropdown.$.bookmarksButton);
        this.hideControl($menuDropdown.$.genresButton);
        this.hideControl($menuDropdown.$.artistsButton);
        this.hideControl($menuDropdown.$.categoryDropdown);
        this.hideControl($menuDropdown.$.brandDropdown);
        this.hideControl($menuDropdown.$.profileButton);
        this.hideControl($menuDropdown.$.privacyButton);
        this.hideControl($menuDropdown.$.faqButton);
        this.hideControl($menuDropdown.$.contactButton);
        this.hideControl($menuDropdown.$.footer);
        this.hideControl($menuDropdown.$.logoutButton);
    },

    /**
     * @private
     */
    visuallyHideHeaderControls: function() {
        var $container = this.container;

        this.visuallyHideControl($container.$.backButton);
        this.visuallyHideControl($container.$.logo);
        this.visuallyHideControl($container.$.previewModeButton);
        this.visuallyHideControl($container.$.searchButton);
        this.visuallyHideControl($container.$.playlistsButton);
        this.visuallyHideControl($container.$.bookmarksButton);
        this.visuallyHideControl($container.$.genresButton);
        this.visuallyHideControl($container.$.artistsButton);
        this.visuallyHideControl($container.$.categoryDropdown);
        this.visuallyHideControl($container.$.brandDropdown);
        this.visuallyHideControl($container.$.signUpButton);
        this.visuallyHideControl($container.$.loginButton);
        this.visuallyHideControl($container.$.languageSwitcher);
        this.visuallyHideControl($container.$.menuDropdown);
    },

    /**
     * @private
     */
    resetHeaderControlsVisibility: function() {
        this.headerControlVisibility = {
            backButton:        false,
            logo:              false,
            previewModeButton: false,
            searchButton:      false,
            playlistsButton:   false,
            bookmarksButton:   false,
            genresButton:      false,
            artistsButton:     false,
            categoryDropdown:  false,
            brandDropdown:     false,
            signUpButton:      false,
            loginButton:       false,
            languageSwitcher:  false,
            menuDropdown:      false
        };
    },

    /**
     * @private
     */
    enableHeaderButtonCaptions: function() {
        var $container = this.container;

        this.removeClass($container.$.searchButton, "app-ui-icon-button--no-caption");
        this.removeClass($container.$.playlistsButton, "app-ui-icon-button--no-caption");
        this.removeClass($container.$.bookmarksButton, "app-ui-icon-button--no-caption");
        this.removeClass($container.$.genresButton, "app-ui-icon-button--no-caption");
        this.removeClass($container.$.artistsButton, "app-ui-icon-button--no-caption");
    },

    /**
     * @returns  {boolean}  Whether the layout succeeded
     * @private
     */
    reflowHeader: function() {
        var $container              = this.container,
            headerControlVisibility = this.headerControlVisibility,
            $buttonRequirements     = $container.$.buttonRequirements,
            $backButton             = $container.$.backButton,
            $menuDropdown           = $container.$.menuDropdown,
            $languageSwitcher       = $container.$.languageSwitcher,
            $logoImage              = $container.$.logoImage.$.logo,
            $logo                   = $container.$.logo,
            $previewModeButton      = $container.$.previewModeButton,
            $searchButton           = $container.$.searchButton,
            $playlistsButton        = $container.$.playlistsButton,
            $bookmarksButton        = $container.$.bookmarksButton,
            $genresButton           = $container.$.genresButton,
            $artistsButton          = $container.$.artistsButton,
            $categoryDropdown       = $container.$.categoryDropdown,
            $brandDropdown          = $container.$.brandDropdown,
            $loginButton            = $container.$.loginButton,
            $signUpButton           = $container.$.signUpButton,
            totalAvailableWidth,
            leftOffset,
            rightOffset,
            availableWidth,
            controlWidth,
            logoImageBounds,
            logoImageRatio,
            logoImageWidth,
            logoImageHeight;

        totalAvailableWidth = $container.getBounds().width;

        // Position Must-have Controls
        // ------------------------

        // Back button is positioned inside the left margin
        if ($buttonRequirements.backButton) {
            controlWidth = $backButton.getBounds().width;

            if (themeVars.formFactor === "large") {
                leftOffset = Math.min(
                    themeVars.appHeaderSpacerGap + controlWidth,
                    Math.max(themeVars.safeAreaInsetLeft, themeVars.getPageMarginLargePx())
                );
            }
            else {
                leftOffset = themeVars.appHeaderSpacerGap + controlWidth;
            }

            $backButton.addStyles({
                left: ((leftOffset / 2) - (controlWidth / 2)) + "px"
            });

            headerControlVisibility.backButton = true;
            availableWidth                     = totalAvailableWidth - leftOffset - rightOffset;
        }
        else {
            if (themeVars.formFactor === "large") {
                leftOffset = Math.max(themeVars.safeAreaInsetLeft, themeVars.getPageMarginLargePx());
            }
            else {
                leftOffset = themeVars.safeAreaInsetLeft + themeVars.getPageMarginLargePx();
            }
        }

        // Menu Dropdown
        controlWidth                         = $menuDropdown.getBounds().width;
        rightOffset                          = themeVars.safeAreaInsetRight + themeVars.appHeaderSpacerGap;
        $menuDropdown.addStyles({
            right: rightOffset + "px"
        });
        headerControlVisibility.menuDropdown = true;
        rightOffset                          += controlWidth;
        availableWidth                       = totalAvailableWidth - leftOffset - rightOffset;

        // Language switcher
        if ($buttonRequirements.languageSwitcher) {
            controlWidth                             = $languageSwitcher.getBounds().width;
            rightOffset                              += themeVars.appHeaderSpacerGap;
            $languageSwitcher.addStyles({
                right: rightOffset + "px"
            });
            headerControlVisibility.languageSwitcher = true;
            rightOffset                              += controlWidth;
            availableWidth                           = totalAvailableWidth - leftOffset - rightOffset;
        }

        // Position Logo
        // ------------------------

        // Clear logo image size to measure it's native size
        $logoImage.addStyles({
            width:  null,
            height: null
        });
        controlWidth = $logo.getBounds().width;

        // Shrink logo proportionally if needed
        logoImageBounds = $logoImage.getBounds();
        logoImageRatio  = logoImageBounds.width / logoImageBounds.height;
        logoImageWidth  = Math.min(controlWidth, availableWidth - themeVars.appHeaderButtonGap);
        logoImageHeight = logoImageWidth / logoImageRatio;
        $logoImage.addStyles({
            width:  logoImageWidth + "px",
            height: logoImageHeight + "px"
        });

        // Place logo
        $logo.addStyles({
            left: leftOffset + "px"
        });
        headerControlVisibility.logo = true;
        leftOffset                   += controlWidth;
        availableWidth               = totalAvailableWidth - leftOffset - rightOffset;

        // Position Wanna-have Controls
        // ------------------------

        // Search Button
        controlWidth = $searchButton.getBounds().width;

        if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
            return false;
        }
        else {
            leftOffset                           += themeVars.appHeaderButtonGap;
            $searchButton.addStyles({
                left: leftOffset + "px"
            });
            headerControlVisibility.searchButton = true;
            leftOffset                           += controlWidth;
            availableWidth                       = totalAvailableWidth - leftOffset - rightOffset;
        }

        // Preview Mode Button
        if ($buttonRequirements.previewModeButton) {
            controlWidth = $previewModeButton.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                leftOffset                                += themeVars.appHeaderButtonGap;
                $previewModeButton.addStyles({
                    left: leftOffset + "px"
                });
                headerControlVisibility.previewModeButton = true;
                leftOffset                                += controlWidth;
                availableWidth                            = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        // SignUp Button
        if ($buttonRequirements.signUpButton) {
            controlWidth = $signUpButton.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                rightOffset                          += themeVars.appHeaderButtonGap;
                $signUpButton.addStyles({
                    right: rightOffset + "px"
                });
                headerControlVisibility.signUpButton = true;
                rightOffset                          += controlWidth;
                availableWidth                       = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        // Login Button
        if ($buttonRequirements.loginButton) {
            controlWidth = $loginButton.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                rightOffset                         += themeVars.appHeaderButtonGap;
                $loginButton.addStyles({
                    right: rightOffset + "px"
                });
                headerControlVisibility.loginButton = true;
                rightOffset                         += controlWidth;
                availableWidth                      = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        // Playlists Button
        if ($buttonRequirements.playlistsButton) {
            controlWidth = $playlistsButton.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                leftOffset                              += themeVars.appHeaderButtonGap;
                $playlistsButton.addStyles({
                    left: leftOffset + "px"
                });
                headerControlVisibility.playlistsButton = true;
                leftOffset                              += controlWidth;
                availableWidth                          = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        // Bookmarks Button
        if ($buttonRequirements.bookmarksButton) {
            controlWidth = $bookmarksButton.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                leftOffset                              += themeVars.appHeaderButtonGap;
                $bookmarksButton.addStyles({
                    left: leftOffset + "px"
                });
                headerControlVisibility.bookmarksButton = true;
                leftOffset                              += controlWidth;
                availableWidth                          = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        // Genres Button
        if ($buttonRequirements.genresButton) {
            controlWidth = $genresButton.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                leftOffset                           += themeVars.appHeaderButtonGap;
                $genresButton.addStyles({
                    left: leftOffset + "px"
                });
                headerControlVisibility.genresButton = true;
                leftOffset                           += controlWidth;
                availableWidth                       = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        // Artists Button
        if ($buttonRequirements.artistsButton) {
            controlWidth = $artistsButton.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                leftOffset                            += themeVars.appHeaderButtonGap;
                $artistsButton.addStyles({
                    left: leftOffset + "px"
                });
                headerControlVisibility.artistsButton = true;
                leftOffset                            += controlWidth;
                availableWidth                        = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        // Category Dropdown
        if ($buttonRequirements.categoryDropdown) {
            controlWidth = $categoryDropdown.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                leftOffset                               += themeVars.appHeaderButtonGap;
                $categoryDropdown.addStyles({
                    left: leftOffset + "px"
                });
                headerControlVisibility.categoryDropdown = true;
                leftOffset                               += controlWidth;
                availableWidth                           = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        // Brand Dropdown
        if ($buttonRequirements.brandDropdown) {
            controlWidth = $brandDropdown.getBounds().width;

            if (controlWidth + (themeVars.appHeaderButtonGap * 2) > availableWidth) {
                return false;
            }
            else {
                leftOffset                            += themeVars.appHeaderButtonGap;
                $brandDropdown.addStyles({
                    left: leftOffset + "px"
                });
                headerControlVisibility.brandDropdown = true;
                leftOffset                            += controlWidth;
                availableWidth                        = totalAvailableWidth - leftOffset - rightOffset;
            }
        }

        return true;
    },

    /**
     * @private
     */
    disableHeaderButtonCaptions: function() {
        var $container = this.container;

        this.ensureClass($container.$.searchButton, "app-ui-icon-button--no-caption");
        this.ensureClass($container.$.playlistsButton, "app-ui-icon-button--no-caption");
        this.ensureClass($container.$.bookmarksButton, "app-ui-icon-button--no-caption");
        this.ensureClass($container.$.genresButton, "app-ui-icon-button--no-caption");
        this.ensureClass($container.$.artistsButton, "app-ui-icon-button--no-caption");
    },

    /**
     * @private
     */
    moveAllButtonsToMenuDropdown: function() {
        this.headerControlVisibility.previewModeButton = false;
        this.headerControlVisibility.searchButton      = false;
        this.headerControlVisibility.playlistsButton   = false;
        this.headerControlVisibility.bookmarksButton   = false;
        this.headerControlVisibility.genresButton      = false;
        this.headerControlVisibility.artistsButton     = false;
        this.headerControlVisibility.categoryDropdown  = false;
        this.headerControlVisibility.brandDropdown     = false;
        this.headerControlVisibility.signUpButton      = false;
        this.headerControlVisibility.loginButton       = false;
    },

    /**
     * @private
     */
    showHeaderControls: function() {
        var that       = this,
            $container = this.container,
            visibility = this.headerControlVisibility;

        Object.keys(visibility).forEach(function(control) {
            that[visibility[control] ? "showControl" : "hideControl"]($container.$[control]);
        });
    },

    /**
     * @private
     */
    showMenuDropdownControls: function() {
        var $container              = this.container,
            $menuDropdown           = $container.$.menuDropdown,
            $buttonRequirements     = $container.$.buttonRequirements,
            headerControlVisibility = this.headerControlVisibility;

        if ($buttonRequirements.previewModeButton && !headerControlVisibility.previewModeButton) {
            this.showControl($menuDropdown.$.previewModeButton);
        }

        if ($buttonRequirements.signUpButton && !headerControlVisibility.signUpButton) {
            this.showControl($menuDropdown.$.signUpButton);
        }

        if ($buttonRequirements.loginButton && !headerControlVisibility.loginButton) {
            this.showControl($menuDropdown.$.loginButton);
        }

        if (!headerControlVisibility.searchButton) {
            this.showControl($menuDropdown.$.searchButton);
        }

        if ($buttonRequirements.playlistsButton && !headerControlVisibility.playlistsButton) {
            this.showControl($menuDropdown.$.playlistsButton);
        }

        if ($buttonRequirements.bookmarksButton && !headerControlVisibility.bookmarksButton) {
            this.showControl($menuDropdown.$.bookmarksButton);
        }

        if ($buttonRequirements.genresButton && !headerControlVisibility.genresButton) {
            this.showControl($menuDropdown.$.genresButton);
        }

        if ($buttonRequirements.artistsButton && !headerControlVisibility.artistsButton) {
            this.showControl($menuDropdown.$.artistsButton);
        }

        if ($buttonRequirements.categoryDropdown && !headerControlVisibility.categoryDropdown) {
            this.showControl($menuDropdown.$.categoryDropdown);
        }

        if ($buttonRequirements.brandDropdown && !headerControlVisibility.brandDropdown) {
            this.showControl($menuDropdown.$.brandDropdown);
        }

        if ($buttonRequirements.profileButton) {
            this.showControl($menuDropdown.$.profileButton);
        }

        if ($buttonRequirements.privacyButton) {
            this.showControl($menuDropdown.$.privacyButton);
        }

        if ($buttonRequirements.faqButton) {
            this.showControl($menuDropdown.$.faqButton);
        }

        if ($buttonRequirements.contactButton) {
            this.showControl($menuDropdown.$.contactButton);
        }

        if ($buttonRequirements.logoutButton) {
            this.showControl($menuDropdown.$.footer);
            this.showControl($menuDropdown.$.logoutButton);
        }
    }

});
